import React, { useState, useRef, useEffect } from 'react';

interface CellContentProps {
  content: any;
  maxWidth?: number;
}

const CellContent: React.FC<CellContentProps> = ({ content, maxWidth = 200 }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<any>(null);
  
  // Convert content to string
  const displayText = content === null || content === undefined ? '' : String(content);
  
  // Check for overflow
  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const element = contentRef.current;
        const isOverflowing = element.scrollWidth > element.clientWidth;
        setShowTooltip(isOverflowing);
        
        // Initialize or destroy Bootstrap tooltip
        if (isOverflowing && tooltipRef.current) {
          // Tooltip already exists, just update content
          tooltipRef.current.setContent({ '.tooltip-inner': displayText });
        } else if (isOverflowing && !tooltipRef.current) {
          // Create new tooltip
          const { Tooltip } = require('bootstrap');
          tooltipRef.current = new Tooltip(element, {
            title: displayText,
            placement: 'top',
            trigger: 'hover',
            container: 'body'
          });
        } else if (!isOverflowing && tooltipRef.current) {
          // Destroy tooltip if no longer overflowing
          tooltipRef.current.dispose();
          tooltipRef.current = null;
        }
      }
    };
    
    // Check after a short delay to ensure element is rendered
    const timeoutId = setTimeout(checkOverflow, 10);
    
    // Also check on resize
    window.addEventListener('resize', checkOverflow);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
      
      // Cleanup tooltip on unmount
      if (tooltipRef.current) {
        tooltipRef.current.dispose();
      }
    };
  }, [displayText]);
  
  // Handle empty content
  if (!displayText.trim()) {
    return <span style={{ color: '#999' }}>-</span>;
  }
  
  // Create content with ellipsis
  return (
    <div
      ref={contentRef}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
      }}
    >
      {displayText}
    </div>
  );
};

export default CellContent;
